import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AuthCallback, ProtectedRoute } from "./components/AuthCallback";
import { Sidebar, MobileNav, MobileHeader } from "./components/Layout";
import { InstallBanner } from "./components/InstallBanner";
import { TrialBanner } from "./components/TrialBanner";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import ClientsPage from "./pages/ClientsPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import ServicesPage from "./pages/ServicesPage";
import ServiceDetailPage from "./pages/ServiceDetailPage";
import InventoryPage from "./pages/InventoryPage";
import FinancePage from "./pages/FinancePage";
import SuppliersPage from "./pages/SuppliersPage";
import ProfilePage from "./pages/ProfilePage";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import TrackingPage from "./pages/TrackingPage";

// Layout wrapper for authenticated pages
const AppLayout = () => {
  return (
    <ProtectedRoute>
      <div className="flex min-h-screen bg-[#F8FAFC] overflow-x-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <MobileHeader />
          <TrialBanner />
          <main className="flex-1 p-4 md:p-8 pb-24 md:pb-8 overflow-x-hidden">
            <Outlet />
          </main>
        </div>
        <MobileNav />
        <InstallBanner />
      </div>
    </ProtectedRoute>
  );
};

// App Router component
const AppRouter = () => {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment synchronously during render for session_id
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/consulta" element={<TrackingPage />} />
      <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
      
      {/* Protected routes with layout */}
      <Route element={<AppLayout />}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route path="/clients/:clientId" element={<ClientDetailPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/services/new" element={<ServicesPage />} />
        <Route path="/services/:serviceId" element={<ServiceDetailPage />} />
        <Route path="/inventory" element={<InventoryPage />} />
        <Route path="/finance" element={<FinancePage />} />
        <Route path="/suppliers" element={<SuppliersPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/subscription" element={<ProfilePage />} />
      </Route>
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
